import { isAxiosError } from "axios"

import { HttpError, toHttpError } from "./api"

export const CSV_EXPORT_BLOB_TYPE = "text/csv; charset=utf-8"
export const XLSX_EXPORT_BLOB_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

const headerString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined

const isCsvContentType = (value: string): boolean => value.includes("text/csv")

const isXlsxContentType = (value: string): boolean =>
  value.includes("spreadsheetml.sheet")

export const exportBlobType = (contentType: unknown): string | undefined => {
  const value = headerString(contentType)
  if (value === undefined) {
    return undefined
  }
  if (isXlsxContentType(value)) {
    return XLSX_EXPORT_BLOB_TYPE
  }
  if (isCsvContentType(value)) {
    return CSV_EXPORT_BLOB_TYPE
  }

  return undefined
}

export const fileNameFromContentDisposition = (
  header: unknown,
  fallback: string
): string => {
  const fileName = /filename="([^"]+)"/.exec(headerString(header) ?? "")?.[1]

  return fileName && fileName.length > 0 ? fileName : fallback
}

const messageFromExportDetail = (body: unknown): string | undefined => {
  if (!body || typeof body !== "object") {
    return undefined
  }

  const detail = (body as { detail?: unknown }).detail
  if (typeof detail === "string") {
    const message = detail.trim()
    return message.length > 0 ? message : undefined
  }
  if (
    detail &&
    typeof detail === "object" &&
    "message" in detail &&
    typeof (detail as { message: unknown }).message === "string"
  ) {
    const message = (detail as { message: string }).message.trim()
    return message.length > 0 ? message : undefined
  }

  return undefined
}

const httpErrorFromBlobResponse = async (
  error: unknown
): Promise<HttpError | undefined> => {
  if (!isAxiosError(error) || !(error.response?.data instanceof Blob)) {
    return undefined
  }

  try {
    const message = messageFromExportDetail(
      JSON.parse(await error.response.data.text()) as unknown
    )
    if (!message) {
      return undefined
    }

    return new HttpError({
      errors: [],
      message,
      status: error.response.status,
      transport: {
        adapter: "axios",
        canceled: false,
        hasResponse: true,
      },
    })
  } catch {
    return undefined
  }
}

export const toExportHttpError = async (error: unknown): Promise<HttpError> => {
  const blobError = await httpErrorFromBlobResponse(error)
  return blobError ?? toHttpError(error)
}
